import { Box, Button, Divider, Link, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { ColDef, ICellRendererParams, SortDirection, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { useEffect, useMemo } from 'react'
import { Link as RouterLink, useParams } from 'react-router'
import { useQuery } from 'urql'
import { useLocalStorage } from 'usehooks-ts'
import { AdminOnly } from '../../components/AdminOnly'
import BigStatCurrency from '../../components/BigStatCurrency'
import DateDisplay from '../../components/DateDisplay'
import { LabeledValue } from '../../components/LabeledValue'
import { NameWithFavicon } from '../../components/NameWithFavicon'
import { NotFound } from '../../components/NotFound'
import ProjectDetailsHeader from '../../components/ProjectDetailsHeader'
import RfqStatusChip from '../../components/RfqStatusChip'
import { ThemedAgGrid } from '../../components/ThemedAgGrid'
import useEventStream from '../../contexts/EventStream/useEventStream'
import { graphql } from '../../gql'
import { ProjectPageQuery } from '../../gql/operations'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { currencyFormatter } from '../../utils/currencyFormatter'
import { parseCurrencyString } from '../../utils/currencyParser'
import { serializeDates } from '../../utils/serialize-dates'

export default function ProjectPage() {
  const setDocumentTitle = useDocumentTitle('Project')
  const params = useParams<{ projectId: string }>()
  if (!params.projectId) {
    throw new Error('Project ID is required')
  }
  const projectId = Number.parseInt(params.projectId, 10)
  const [pageQueryResult, refreshPageQuery] = useQuery({
    query: graphql(/* GraphQL */ `
      query ProjectPage($projectId: Int!) {
        Project(id: $projectId) {
          id
          name
          dateCreated
          outstandingCommission
          incomePotential
          totalPaymentsReceived
          client {
            id
            name
          }
          rfqs {
            id
            name
            dateCreated
            dateReceived
            status
            quote {
              amount
              purchaseOrder {
                status
                amount
                commissionPercentage
                totalReceived
              }
            }
            vendor {
              id
              name
              website
            }
          }
        }
      }
    `),
    variables: { projectId },
    requestPolicy: 'network-only',
  })

  useEventStream({
    onMessage: (message) => {
      const e = message?.event
      if (e && (e.startsWith('project.') || e === 'rfq.updated')) {
        console.log('Refreshing page query')
        setTimeout(() => refreshPageQuery(), 100)
      }
    },
  })

  const project = pageQueryResult.data?.Project

  useEffect(() => {
    if (project) {
      setDocumentTitle(`Project: ${project.name}`)
    }
  }, [project, setDocumentTitle])

  if (pageQueryResult.fetching) {
    return (
      <Box display="flex" flexDirection="column" flex="1">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton variant="text" width="200px" height="32px" />
        </Stack>
      </Box>
    )
  }
  if (pageQueryResult.error) {
    if (pageQueryResult.error.message?.toLowerCase().includes('not found')) {
      return <NotFound title="Project not found" />
    }
    return <Typography>Error loading project</Typography>
  }
  if (!project) {
    return <NotFound />
  }
  return (
    <Stack spacing={2} flex="1">
      <ProjectDetailsHeader proj={project} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        divider={<Divider sx={{ borderBottomWidth: 0, borderRightWidth: { xs: 0, md: 'thin' } }} />}
        component={Paper}
        p={2}
        maxWidth="100vw"
      >
        <LabeledValue label="Client">
          <Link component={RouterLink} to={`/clients/${project.client?.id}`}>
            {project.client?.name}
          </Link>
        </LabeledValue>
        <LabeledValue label="Date Created">
          <DateDisplay absolute date={project.dateCreated} />
        </LabeledValue>
        {project.outstandingCommission !== undefined && (
          <AdminOnly>
            <LabeledValue label="Outstanding Commission">
              <BigStatCurrency
                value={parseCurrencyString(project.outstandingCommission)}
                color="success"
                sx={{ fontSize: '1.5em', pt: 0.5 }}
                minimumSignificantDigits={2}
              />
            </LabeledValue>
          </AdminOnly>
        )}
        {project.incomePotential !== undefined && (
          <LabeledValue label="Income Potential">
            <BigStatCurrency
              value={parseCurrencyString(project.incomePotential)}
              color="success"
              sx={{ fontSize: '1.5em', pt: 0.5 }}
              minimumSignificantDigits={2}
            />
          </LabeledValue>
        )}
        {project.totalPaymentsReceived !== undefined && (
          <LabeledValue label="Total Payments Received">
            <BigStatCurrency
              value={parseCurrencyString(project.totalPaymentsReceived)}
              color="success"
              sx={{ fontSize: '1.5em', pt: 0.5 }}
              minimumSignificantDigits={2}
            />
          </LabeledValue>
        )}
      </Stack>
      <Stack direction="row" spacing={4} pt={2}>
        <Typography variant="h4">Orders</Typography>
        <Button component={RouterLink} to={`/rfqs/new?project=${projectId}`} variant="contained" color="secondary">
          Add RFQ
        </Button>
      </Stack>
      <RFQTable rfqs={project.rfqs} />
      <Typography variant="h4">Other stats</Typography>
      <Paper sx={{ p: 2 }}>
        <Typography variant="body1">Coming soon</Typography>
        <Typography variant="body1" component="div">
          We can show things like:
          <ul>
            <li>Total estimated commission</li>
            <li>Outstanding payments</li>
            <li>Payments received</li>
          </ul>
        </Typography>
      </Paper>
    </Stack>
  )
}

const RFQTable = ({ rfqs }: { rfqs?: ProjectPageQuery['Project']['rfqs'] }) => {
  if (!rfqs) return null
  const [defaultSort, setDefaultSort] = useLocalStorage<{ colId: string; dir: SortDirection | undefined }>(
    'ProjectPage.RFQTable.sort',
    {
      colId: 'dateReceived',
      dir: 'desc',
    },
  )
  const rowData = rfqs.map(serializeDates)
  type TData = (typeof rowData)[0]

  const colDefs = useMemo<ColDef[]>(() => {
    const defs: ColDef[] = [
      {
        field: 'id',
        headerName: '',
        width: 90,
        sortable: false,
        cellRenderer: (d: ICellRendererParams<TData>) => {
          return (
            <Link component={RouterLink} to={`/order/${d.data!.id}`}>
              Details
            </Link>
          )
        },
      },
      {
        colId: 'name',
        field: 'name',
        filter: true,
      },
      {
        colId: 'vendor.name',
        field: 'vendor.name',
        headerName: 'Vendor',
        cellRenderer: (d: ICellRendererParams<TData>) => {
          const v = d.data?.vendor
          if (!v) return null
          return <NameWithFavicon name={v.name} website={v.website} />
        },
      },
      {
        colId: 'status',
        field: 'status',
        headerName: 'Status',
        filter: true,
        cellRenderer: (d: ICellRendererParams<TData>) => {
          return <RfqStatusChip status={d.data!.status} size="small" />
        },
      },
      {
        colId: 'quote.amount',
        field: 'quote.amount',
        headerName: 'Quote Amount',
        type: 'rightAligned',
        valueFormatter: (params: ValueFormatterParams<TData>) => {
          return params.value ? currencyFormatter.format(params.value) : '--'
        },
      },
      {
        colId: 'quote.purchaseOrder.amount',
        field: 'quote.purchaseOrder.amount',
        headerName: 'PO Amount',
        type: 'rightAligned',
        valueFormatter: (params: ValueFormatterParams<TData>) => {
          return params.value ? currencyFormatter.format(params.value) : '--'
        },
      },
      {
        colId: 'expectedCommission',
        type: 'rightAligned',
        headerName: 'Expected Commission',
        valueGetter: (params: ValueGetterParams<TData>) => {
          const po = params.data!.quote?.purchaseOrder
          if (!po || !po.commissionPercentage) return undefined
          return po.amount * (po.commissionPercentage / 100)
        },
        valueFormatter: (params: ValueFormatterParams<TData>) => {
          return params.value ? currencyFormatter.format(params.value) : '--'
        },
      },
      {
        colId: 'dateReceived',
        field: 'dateReceived',
        cellDataType: 'date',
        cellRenderer: (d: ICellRendererParams<TData>) => {
          return <DateDisplay date={d.data!.dateReceived} absolute />
        },
      },
      {
        colId: 'dateCreated',
        field: 'dateCreated',
        cellDataType: 'date',
        cellRenderer: (d: ICellRendererParams<TData>) => {
          return <DateDisplay date={d.data!.dateCreated} absolute />
        },
      },
    ]
    if (defaultSort) {
      const d = defs.find((c) => c.colId === defaultSort.colId)
      if (d) {
        d.sort = defaultSort.dir
      }
    }
    return defs
  }, [defaultSort])

  return (
    <ThemedAgGrid
      rowData={rowData}
      domLayout="autoHeight"
      onSortChanged={(e) => {
        try {
          if (e.columns?.[0]) {
            const c = e.columns[0]
            const colId = c.getColId()
            const dir = c.getSort()
            if (colId) {
              setDefaultSort({ colId, dir })
            }
          }
        } catch (e) {
          console.error(e)
        }
      }}
      autoSizeStrategy={{
        type: 'fitGridWidth',
        columnLimits: [
          { colId: 'id', maxWidth: 90, minWidth: 80 },
          { colId: 'name', maxWidth: 200 },
          { colId: 'status', maxWidth: 120, minWidth: 120 },
        ],
      }}
      defaultColDef={{
        filter: false,
        sortable: true,
        editable: false,
      }}
      columnDefs={colDefs}
    />
  )
}
